import { useEffect, useMemo, useState } from 'react';
import { getFreshFileUrl } from 'utils';
import { useFormBuilder } from '../formDetail/formContext';

const IMG_DATA_KEY_RE = /<img\b[^>]*\bdata-key="([^"]+)"/gi;

// One request per key while it is in flight. A non-continuous preview renders
// the same header on every section page — they all share one mint.
const pendingMints = new Map();

const mintUrl = (key) => {
    if (!pendingMints.has(key)) {
        pendingMints.set(
            key,
            getFreshFileUrl({ key }).finally(() => pendingMints.delete(key)),
        );
    }
    return pendingMints.get(key);
};

const extractImageKeys = (html) => {
    if (!html || typeof html !== 'string' || html.indexOf('data-key=') === -1) return [];
    return Array.from(html.matchAll(IMG_DATA_KEY_RE), (m) => m[1]);
};

/**
 * The `imageUrls` map a read-only rich-text render needs to resolve the
 * `<img data-key>` tags in `html` — the same map `RichTextElement` resolves
 * from: the FormBuilderContext `imageUrls` (server-minted on GET, plus
 * images uploaded this session via `onImageUpload`).
 *
 * Any key in `html` that the map does not hold (the GET response mints only
 * section images, so header/footer images are missing after a reload) is
 * minted through `POST /files/download-url` and registered into the context
 * map, so every other consumer of that map resolves it too.
 */
export const useRichTextImageUrls = (html, imageUrlsOverride) => {
    const { imageUrls: contextImageUrls, setImageUrls } = useFormBuilder();
    // Also kept locally — outside a FormBuilderProvider `setImageUrls` is a no-op.
    const [mintedUrls, setMintedUrls] = useState({});
    const imageUrls = useMemo(
        () => ({ ...mintedUrls, ...(contextImageUrls || {}), ...(imageUrlsOverride || {}) }),
        [mintedUrls, contextImageUrls, imageUrlsOverride],
    );

    const missingKeys = extractImageKeys(html)
        .filter((key) => !imageUrls[key])
        .join('|');

    useEffect(() => {
        if (!missingKeys) return;
        let cancelled = false;
        missingKeys.split('|').forEach((key) => {
            mintUrl(key).then((url) => {
                if (cancelled || !url) return;
                setMintedUrls((prev) => ({ ...prev, [key]: url }));
                setImageUrls?.((prev) => (prev?.[key] ? prev : { ...prev, [key]: url }));
            });
        });
        return () => {
            cancelled = true;
        };
    }, [missingKeys, setImageUrls]);

    return imageUrls;
};
